"use client";
// Imports
import { getDynamicCountdown } from "@/lib/helpers";
import { formatShowDateRangeLabel } from "@/lib/showDates";
import { useEffect, useState } from "react";

function isCountdownFinished(days, hours, minutes, seconds) {
  if (days == null || hours == null || minutes == null || seconds == null) {
    return false;
  }
  return days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0;
}

function shouldShowLiveCountdown(showCount, days, hours, minutes, seconds) {
  if (!showCount || days == null) return false;
  if (isCountdownFinished(days, hours, minutes, seconds)) return false;
  return days <= 100;
}

function readCountdown(unformattedStartDate, showStartTime, showCount) {
  if (!unformattedStartDate) {
    return {
      days: null,
      hours: null,
      minutes: null,
      seconds: null,
      showLive: false,
    };
  }

  const { days, hours, minutes, seconds } = getDynamicCountdown(
    unformattedStartDate,
    showStartTime
  );

  return {
    days,
    hours,
    minutes,
    seconds,
    showLive: shouldShowLiveCountdown(
      showCount,
      days,
      hours,
      minutes,
      seconds
    ),
  };
}

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center justify-center w-14 h-14 sm:w-[4.5rem] sm:h-[4.5rem] md:w-20 md:h-20 lg:w-[5.25rem] lg:h-[5.25rem] rounded-lg bg-brand-wool px-1 py-1 sm:px-1.5 sm:py-1.5">
      <span
        suppressHydrationWarning
        className="font-heading text-[clamp(1.05rem,2.8vw+0.55rem,2.15rem)] leading-none text-brand-orange tabular-nums"
      >
        {value ?? "–"}
      </span>
      <span className="mt-0.5 sm:mt-1 font-body text-[clamp(0.55rem,0.35vw+0.45rem,0.75rem)] uppercase tracking-wide text-brand-orange leading-none">
        {label}
      </span>
    </div>
  );
}

function ShowDatesBanner({ dateRange }) {
  if (!dateRange) return null;

  return (
    <div className="w-full bg-brand-red-gum text-white">
      <div className="flex items-center justify-center px-4 py-3.5 sm:px-6 sm:py-2.5 lg:px-10 lg:py-3">
        <p className="font-heading text-sm sm:text-base lg:text-xl tracking-wide text-center text-brand-wool">
          {dateRange}
        </p>
      </div>
    </div>
  );
}

export function Countdown({
  unformattedStartDate,
  unformattedEndDate,
  showStartTime,
  showCount,
}) {
  const [tick, setTick] = useState(() =>
    readCountdown(unformattedStartDate, showStartTime, showCount)
  );

  const { days, hours, minutes, seconds, showLive } = tick;

  useEffect(() => {
    setTick(readCountdown(unformattedStartDate, showStartTime, showCount));
  }, [unformattedStartDate, showStartTime, showCount]);

  useEffect(() => {
    if (!unformattedStartDate || !showLive) return;

    const interval = setInterval(() => {
      setTick(readCountdown(unformattedStartDate, showStartTime, showCount));
    }, 1000);

    return () => clearInterval(interval);
  }, [unformattedStartDate, showStartTime, showCount, showLive]);

  const bannerDateRange = formatShowDateRangeLabel(
    unformattedStartDate,
    unformattedEndDate
  );

  if (!unformattedStartDate || !unformattedEndDate) {
    return null;
  }

  if (!showLive) {
    return <ShowDatesBanner dateRange={bannerDateRange} />;
  }

  return (
    <div className="w-full bg-brand-red-gum text-white">
      <div className="flex flex-col sm:flex-row items-center sm:justify-end gap-2.5 sm:gap-4 lg:gap-5 px-4 py-3.5 sm:px-6 sm:py-2.5 lg:px-10 lg:py-3">
        <p className="font-heading text-sm sm:text-base lg:text-lg uppercase tracking-wide leading-none text-center sm:text-left shrink-0 text-brand-wool">
          Gates open in
        </p>
        <div className="flex items-center justify-center gap-1.5 sm:gap-2.5 lg:gap-3">
          <CountdownUnit value={days} label="Days" />
          <CountdownUnit value={hours} label="Hours" />
          <CountdownUnit value={minutes} label="Minutes" />
          <CountdownUnit value={seconds} label="Seconds" />
        </div>
      </div>
    </div>
  );
}
